import PixelCredit from '../../../assets/icons/pixel-credit.svg';
import PixelCoin from '../../../assets/icons/coin_icon.svg';
import {Button} from '../../../ui-components/Button/button';
import React, {useEffect, useRef, useState} from 'react';
import {nFetch} from '../../../api/nFetch';
import Tracker from '../../../lib/analytics/tracker';
import {ErrorTags, EventNames, EventProperty} from '../../../lib/analytics/events';
import {toast} from 'react-toastify';
import {getWalletBalance} from '../../../actions/metrics';
import {useAppDispatch} from '../../../store';
import {Invoice} from '../../../api/types/billing';
import {refreshUserProfile} from '../../../actions/userProfile';
import {InvoicePaymentHandler} from '../billing/InvoicePaymentHandler';
import {loadScript} from '../../../lib/load-script';

interface AddCreditsProps {
    walletBalance: number;
    org_uid: string;
    resetCouponState: () => void;
}

function PlanInvoiceHandler(props: {
    invoice: Invoice;
    setLoading: (loading: boolean) => void;
    onSuccess: (success: boolean) => void;
}) {
    return new InvoicePaymentHandler({
        invoice: props.invoice,
        onNext: (invoice) => {
            if (invoice.status === 'PAID') {
                toast.success('Payment successful!');
                props.onSuccess(true);
            } else if (invoice.status === 'CANCELLED') {
                toast.error(`Payment cancelled. ${InvoicePaymentHandler.REFUND_MSG}`);
                props.onSuccess(false);
            } else {
                toast.error(`Payment failed. ${InvoicePaymentHandler.REFUND_MSG}`);
                props.onSuccess(false);
            }
        },
        onError: (error) => {
            toast.error(error.message);
            props.onSuccess(false);
        },
        onLoading: (loading) => {
            props.setLoading(loading);
        },
        onProcessing: (processing) => {
            if (processing) {
                toast.info('Processing payment...');
            }
        },
        cancelInvoice: true,
    });
}

export default function AddCredits({walletBalance, org_uid, resetCouponState}: AddCreditsProps) {
    const [topUpAmount, setTopUpAmount] = useState<number | undefined>();
    const [couponCode, setCouponCode] = useState<string | undefined>();
    const [invalidCoupon, setInvalidCoupon] = useState(false);
    const [submittingCoupon, setSubmittingCoupon] = useState(false);
    const [invoice, setInvoice] = useState<Invoice | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [rzpScriptReady, setRzpScriptReady] = React.useState(false);
    const mut = useRef(false);
    const pmntMut = useRef<InvoicePaymentHandler | undefined>(undefined);

    const dispatch = useAppDispatch();

    const topUpValues = [100, 150, 300, 500, 1000];

    const resetCoupon = () => {
        setCouponCode('');
        setInvalidCoupon(false);
        setSubmittingCoupon(false);
    };

    const addCreditsToWallet = () => {
        if (!couponCode) return;
        setInvalidCoupon(false);
        setSubmittingCoupon(true);
        nFetch<any>(`/reward/wallet`, {
            method: 'POST',
            body: JSON.stringify({
                org_uid: org_uid,
                coupon: couponCode,
            }),
        })
            .then((res) => {
                if (!!res.error) {
                    setInvalidCoupon(true);
                } else {
                    Tracker.trackEvent(EventNames.CREDITS_REDEEM_SUCCESS, {
                        [EventProperty.CouponName]: couponCode,
                    });
                    toast.success('Successfully added balance to wallet');
                    resetCoupon();
                    resetCouponState();
                    dispatch(getWalletBalance({org_uid}));
                }
                return res.data!;
            })
            .finally(() => setSubmittingCoupon(false));
    };

    const rechargeWallet = async () => {
        if (!topUpAmount) return;
        const result = await nFetch<Invoice>(
            `/billing/start_for_org_wallet/${org_uid}`,
            {
                method: 'POST',
                body: JSON.stringify({
                    tier: 'TOPUP',
                    billing_cycle: 'MONTHLY',
                    amount: topUpAmount,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
            undefined,
            false,
        );
        if (!!result.error) {
            Tracker.trackError(result.error, ErrorTags.INVOICE_START_ERROR);
            toast.error(result.error.message);
            return;
        }
        resetCouponState();
        setInvoice(result.data);
    };

    useEffect(() => {
        if (mut.current) {
            return;
        }
        mut.current = true;
        loadScript('https://checkout.razorpay.com/v1/checkout.js')
            .then((loaded) => {
                setRzpScriptReady(loaded);
            })
            .catch((err) => {
                console.error('failed to load razorpay script', err);
                setRzpScriptReady(false);
            });
    });

    useEffect(() => {
        if (invoice) {
            pmntMut.current = PlanInvoiceHandler({
                invoice: invoice,
                setLoading: setLoading,
                onSuccess: (success) => {
                    setLoading(false);
                    if (success) {
                        dispatch(getWalletBalance({org_uid}));
                    } else {
                        setInvoice(undefined);
                    }
                },
            });
            pmntMut.current?.next();
        }
    }, [invoice]);

    useEffect(() => {
        setInvalidCoupon(false);
    }, [couponCode]);

    return (
        <div>
            <div className='flex items-center justify-between px-[61px] py-7'>
                <img src={PixelCredit} alt='pixelcredit' />
                <div className='flex items-center justify-center gap-2 pt-[51px] text-[18px] font-[400]'>
                    <span>Available Balance:</span>
                    <div className='flex items-center justify-center gap-1 font-[700]'>
                        <span>{walletBalance}</span>
                        <img src={PixelCoin} alt='pixelcoin' />
                    </div>
                </div>
            </div>
            <div className='border-t border-[#D0D5DD]'>
                <div className='flex items-start px-[61px] py-[33px] font-[700]'>
                    <div className='flex flex-col border-r border-[#D0D5DD] pb-4 pr-[44px]'>
                        <span>Top-up</span>
                        <div className='grid grid-cols-4 gap-y-2 pt-10'>
                            {topUpValues.map((value) => (
                                <Button
                                    key={value}
                                    variant='secondary'
                                    className={`text-[14px] font-[600] text-gray-700 ${topUpAmount === value ? 'border border-[#B2CCFF] bg-[#EFF4FF]' : ''}`}
                                    onClick={() => setTopUpAmount(value)}>
                                    ${value}
                                </Button>
                            ))}
                        </div>
                        <div className='my-4 rounded-[8px] border border-[#D0D5DD] px-[14px] py-[10px] shadow-sm'>
                            <input
                                type='number'
                                value={topUpAmount ?? ''}
                                placeholder='Enter amount (Eg: $1)'
                                className='font-[400] text-gray-700 placeholder-[#98A2B3] outline-none [&::-webkit-inner-spin-button]:appearance-none'
                                onChange={(e) => setTopUpAmount(Number(e.target.value))}
                            />
                        </div>
                        <Button
                            variant='primary'
                            className={`w-full text-[14px] font-[600] ${topUpAmount ? 'cursor-pointer bg-primary hover:bg-primary-hover' : 'cursor-auto bg-[#BABABA] hover:bg-[#BABABA]'}`}
                            onClick={rechargeWallet}>
                            Top-Up
                        </Button>
                    </div>
                    <div className='flex flex-col pl-9'>
                        <span>Redeem Coupon</span>
                        <div className='gap-2 pt-10 font-[400]'>
                            <span className='text-[14px] text-gray-700'>Enter coupon code</span>
                            <div className=' mt-2 w-[264px] rounded-[8px] border border-[#D0D5DD] px-[14px] py-[10px] shadow-sm'>
                                <input
                                    type='text'
                                    value={couponCode ?? ''}
                                    placeholder='Ex: XY67453RTE'
                                    className='font-[400] text-gray-700 placeholder-[#98A2B3] outline-none [&::-webkit-inner-spin-button]:appearance-none'
                                    onChange={(e) => setCouponCode(e.target.value)}
                                />
                            </div>
                            <div
                                className={`mb-6 mt-[6px] text-[14px] font-[400] text-[#F04438] ${invalidCoupon ? 'opacity-100' : 'opacity-0'}`}>
                                <span>The coupon code is invalid</span>
                            </div>

                            <Button
                                variant='secondary'
                                className={`w-full px-[14px] py-2 text-[14px] font-[600] text-white ${couponCode ? 'cursor-pointer bg-primary hover:bg-primary-hover' : 'cursor-auto bg-[#BABABA] hover:bg-[#BABABA]'}`}
                                disabled={!couponCode}
                                onClick={addCreditsToWallet}>
                                Redeem Coupon
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
